import { DeleteDialog, GlobalMenu } from '@components';
import { NavigationContext } from '@layout/navigation/navigation';
import { IDocument } from '@models/Document.model';
import { AssetContext } from '@pages/Asset/Asset.query';
import { deleteDocument } from '@services/queries/documents';
import { getPermissionData } from '@utils';
import { useContext } from 'react';
import { useState } from '@hookstate/core';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { getProductValue } from '@utils/getProductValue';
import { Container } from './DocumentsItemMenu.styles';

interface IDocumentsItemMenu {
  document: IDocument;
}

const DocumentsItemMenu = ({ document }: IDocumentsItemMenu) => {
  const params = useParams<{ equipmentId: string }>();

  const { selectedDocument, openEditDocument, equipmentQuery } =
    useContext(AssetContext);
  const { role } = useContext(NavigationContext);
  const equipment = equipmentQuery?.data;
  const queryClient = useQueryClient();
  const openDeleteDialog = useState(false);

  const mutation = useMutation(deleteDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'documents',
      ]);
      openDeleteDialog.set(false);
    },
  });

  const editPermission = getPermissionData({
    owner: Boolean(document.ownerStudioId),
    action: 'edit',
    role,
    type: 'Document',
  });

  const { manufacturerId, productId } = getProductValue(equipment);

  const hasProduct = manufacturerId && productId;
  const isEnabled = editPermission.allowed && hasProduct;

  const deletePermission = getPermissionData({
    owner: Boolean(document.ownerStudioId),
    action: 'delete',
    role,
    type: 'Document',
  });

  return (
    <Container>
      <GlobalMenu
        align="end"
        title="Options"
        itemList={[
          {
            disabled: !isEnabled,
            disableToolTip: editPermission.allowed,
            label: 'Edit',
            iconName: 'edit',
            toolTipText: editPermission.message,
            toolTipId: 'editDocumentTooltip',
            onClick: () => {
              if (isEnabled) {
                selectedDocument.set(document);
                openEditDocument.set(true);
              }
            },
          },
          {
            label: 'Delete',
            iconName: 'trash',
            disabled: !deletePermission.allowed,
            toolTipText: deletePermission.message,
            toolTipId: 'deleteDocumentTooltip',
            disableToolTip: deletePermission.allowed,
            onClick: () => {
              if (deletePermission.allowed) {
                openDeleteDialog.set(true);
              }
            },
          },
        ]}
      />

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutation.mutate(document.id);
        }}
      />
    </Container>
  );
};

export { DocumentsItemMenu };
